// Reversible theming. The default theme is the existing design (no attribute);
// alternate themes are applied as a `data-theme` attribute on <html>, which
// re-points the CSS design tokens. Remove the attribute → back to default.
const KEY = 'roamr-theme';
export const THEMES = ['default', 'auris'];

export function getTheme() {
  const p = new URLSearchParams(window.location.search).get('theme');
  if (p && THEMES.includes(p)) return p;
  try { return localStorage.getItem(KEY) || 'auris'; } catch { return 'auris'; }
}

export function applyTheme(name) {
  const t = THEMES.includes(name) ? name : 'default';
  if (t === 'default') document.documentElement.removeAttribute('data-theme');
  else document.documentElement.setAttribute('data-theme', t);
  try { localStorage.setItem(KEY, t); } catch { /* ignore */ }
  // Re-apply accent: an unset custom accent falls back to the new theme's
  // default (orange for light, gold for dark) — and refreshes the cursor.
  applyAccent(getAccent());
}

// ── Accent colour ──────────────────────────────────────────────────────────
// One custom accent drives BOTH themes via the inline --accent var (consumed in
// variables.css + auris.css). When unset, each theme uses its own default.
const ACCENT_KEY = 'roamr-auris-accent';
export const LIGHT_DEFAULT_ACCENT = '#ff7a1a'; // light theme default (orange)
export const DARK_DEFAULT_ACCENT  = '#F0A500'; // dark/AURIS default (gold)
export const DEFAULT_ACCENT = DARK_DEFAULT_ACCENT; // back-compat export
export const ACCENT_PRESETS = ['#ff7a1a', '#F0A500', '#FF5E3A', '#36D399', '#3ABFF8', '#C084FC', '#F471B5'];

// The active theme's default accent (used when the user hasn't picked a custom one).
export function themeDefaultAccent() {
  return getTheme() === 'auris' ? DARK_DEFAULT_ACCENT : LIGHT_DEFAULT_ACCENT;
}

// Stored custom accent, or null if the user hasn't chosen one.
export function getAccent() {
  try { return localStorage.getItem(ACCENT_KEY) || null; } catch { return null; }
}

// WCAG relative luminance → pick the on-accent text colour (near-black or warm
// near-white) with the better contrast, so foreground stays readable on ANY
// custom accent the user chooses.
export function contrastFor(color) {
  const h = String(color || '').replace('#', '');
  if (h.length < 6) return '#0A0A0C';
  const ch = i => {
    const v = parseInt(h.slice(i, i + 2), 16) / 255;
    return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
  };
  const L = 0.2126 * ch(0) + 0.7152 * ch(2) + 0.0722 * ch(4);
  const contrastBlack = (L + 0.05) / 0.05;
  const contrastWhite = 1.05 / (L + 0.05);
  return contrastBlack >= contrastWhite ? '#0A0A0C' : '#F5F0E6';
}

export function applyAccent(color) {
  const effective = color || themeDefaultAccent();
  const root = document.documentElement.style;
  // Unified source for both themes + the auto-contrast on-accent text colour.
  root.setProperty('--accent', effective);
  root.setProperty('--accent-contrast', contrastFor(effective));
  applyMapCursor();
}

export function saveAccent(color) {
  try { localStorage.setItem(ACCENT_KEY, color); } catch { /* ignore */ }
  applyAccent(color);
}

export function resetAccent() {
  try { localStorage.removeItem(ACCENT_KEY); } catch { /* ignore */ }
  applyAccent(null); // falls back to the active theme's default accent
}

// ── Map edit-mode cursor ────────────────────────────────────────────────────
// Built in JS so the shape colour follows the live theme + accent:
//   dark (AURIS) → hexagon reticle, default → circle reticle; both in the
// current accent colour. A dark outer stroke keeps it visible on any tiles;
// the centre dot is fixed amber. Exposed as the --map-cursor-edit CSS var.
function buildMapCursor({ shape, color }) {
  const reticle = shape === 'hex'
    ? '<polygon points="14,4 22.66,9 22.66,19 14,24 5.34,19 5.34,9" fill="none" stroke="#0A0A0C" stroke-width="3.4" stroke-linejoin="round"/>'
      + `<polygon points="14,4 22.66,9 22.66,19 14,24 5.34,19 5.34,9" fill="none" stroke="${color}" stroke-width="1.8" stroke-linejoin="round"/>`
    : '<circle cx="14" cy="14" r="9" fill="none" stroke="#0A0A0C" stroke-width="3.4"/>'
      + `<circle cx="14" cy="14" r="9" fill="none" stroke="${color}" stroke-width="1.8"/>`;
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 28 28">${reticle}<circle cx="14" cy="14" r="2" fill="#F0A500" stroke="#0A0A0C" stroke-width="0.8"/></svg>`;
  return `url("data:image/svg+xml;base64,${btoa(svg)}") 14 14, crosshair`;
}

export function applyMapCursor() {
  const isAuris = getTheme() === 'auris';
  const color = getAccent() || themeDefaultAccent();
  const value = buildMapCursor({ shape: isAuris ? 'hex' : 'circle', color });
  document.documentElement.style.setProperty('--map-cursor-edit', value);
}
